import React, { useState, forwardRef, ReactNode } from 'react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { Search1Stroke } from '@lineiconshq/free-icons';
import { cn } from '@/lib/utils';

export interface BaseSearchTriggerProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value: string;
  hasValue?: boolean;
  isOpen?: boolean;
  isFocused?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rightContent?: ReactNode;
}

export const BaseSearchTrigger = forwardRef<HTMLInputElement, BaseSearchTriggerProps>(
  ({ value, hasValue, isOpen, isFocused, onChange, rightContent, className = '', ...inputProps }, ref) => {
    const [showIcon, setShowIcon] = useState(false);
    const internalHasValue = hasValue !== undefined ? hasValue : !!value;
    const isIconActive = isFocused || isOpen || showIcon || internalHasValue;

    return (
      <div
        className={cn(
          'base-search-trigger group relative flex h-full w-full items-center rounded bg-transparent px-1.5 transition-all',
          className
        )}
        onMouseEnter={() => setShowIcon(true)}
        onMouseLeave={() => setShowIcon(false)}
      >
        <div className='relative flex h-full w-full flex-row items-center'>
          {!rightContent && (
            <Lineicons
              aria-hidden='true'
              icon={Search1Stroke}
              size={13}
              className='absolute left-1.5 z-10 opacity-35 transition-opacity duration-150 group-focus-within:opacity-100'
            />
          )}

          <input
            ref={ref}
            type='text'
            value={value}
            onChange={onChange}
            placeholder={inputProps.placeholder}
            className={cn(
              'h-full w-full cursor-text border-none bg-transparent px-1 text-left text-xs font-semibold text-gray-800 outline-none transition-all placeholder:font-normal placeholder:text-gray-300',
              rightContent ? 'pl-1 pr-12' : 'pl-6',
              className
            )}
            {...inputProps}
          />

          {rightContent && !isFocused && !isOpen && (
            <div className='pointer-events-none absolute right-0 top-0 flex h-full items-center gap-1 font-mono text-[11px] text-gray-500'>
              {rightContent}
            </div>
          )}
        </div>
      </div>
    );
  }
);

BaseSearchTrigger.displayName = 'BaseSearchTrigger';
